import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  UserPlus,
  Clock,
  CheckCircle2,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Loader2,
  Info,
  Users
} from 'lucide-react';
import { authenticatedFetch } from '../lib/auth';

type SessionStatus =
  | 'unassigned'
  | 'no_progress'
  | 'in_progress'
  | 'ready_to_finalize'
  | 'no_answers';

interface AssignedSensor {
  id: string;
  email: string;
  full_name: string | null;
}

interface GradingSession {
  session_id: string;
  exam_set_id: string;
  exam_set_name: string | null;
  course_instance_id: string;
  course_instance_name: string;
  member_id: string | null;
  member_name: string | null;
  study_association_id: string | null;
  association_name: string | null;
  submitted_at: string | null;
  candidate_code: string | null;
  assigned_sensors: AssignedSensor[];
  total_answers: number;
  graded_answers: number;
  progress_pct: number;
  status: SessionStatus;
}

interface GradingCourse {
  course_instance_id: string;
  course_instance_name: string;
  exam_set_name: string | null;
  member_id: string | null;
  member_name: string | null;
  study_association_id: string | null;
  association_name: string | null;
  assigned_sensors: AssignedSensor[];
  course_total_candidates: number;
  awaiting_sensur: number;
  counts: {
    unassigned: number;
    no_progress: number;
    in_progress: number;
    ready_to_finalize: number;
    no_answers: number;
  };
  oldest_submitted_at: string | null;
  newest_submitted_at: string | null;
  course_status: SessionStatus;
}

interface Summary {
  total: number;
  courses_total: number;
  unassigned: number;
  no_progress: number;
  in_progress: number;
  ready_to_finalize: number;
  no_answers: number;
}

const STATUS_META: Record<SessionStatus, { label: string; color: string; icon: JSX.Element; help: string }> = {
  unassigned: {
    label: 'Sensor mangler',
    color: 'bg-red-100 text-red-800 border-red-200',
    icon: <UserPlus className="h-4 w-4" />,
    help: 'Kurset har ingen tildelt sensor — besvarelsen kan ikke sensureres før noen tildeles.'
  },
  no_progress: {
    label: 'Ikke påbegynt',
    color: 'bg-amber-100 text-amber-800 border-amber-200',
    icon: <Clock className="h-4 w-4" />,
    help: 'Sensor er tildelt, men har ikke sensurert noen svar enda.'
  },
  in_progress: {
    label: 'Underveis',
    color: 'bg-kf-blue-100 text-kf-blue-800 border-kf-blue-200',
    icon: <Loader2 className="h-4 w-4" />,
    help: 'Sensor har startet — noen svar er sensurert, resten gjenstår.'
  },
  ready_to_finalize: {
    label: 'Klar til finalisering',
    color: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    icon: <CheckCircle2 className="h-4 w-4" />,
    help: 'Alle svar er sensurert. Sensor må klikke "Fullfør sensur" for at resultat blir utstedt.'
  },
  no_answers: {
    label: 'Ingen svar',
    color: 'bg-slate-100 text-slate-800 border-slate-200',
    icon: <AlertCircle className="h-4 w-4" />,
    help: 'Besvarelsen har ingen registrerte svar. Vurder alternativ vurdering eller nullstilling.'
  }
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('nb-NO', {
    timeZone: 'Europe/Oslo',
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
}

export default function GradingOverview() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<GradingSession[]>([]);
  const [courses, setCourses] = useState<GradingCourse[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [filter, setFilter] = useState<SessionStatus | 'all'>('all');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await authenticatedFetch('/api/admin/grading-overview');
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        if (r.status === 403 && body.error === 'sensor_use_dashboard') {
          setError('Sensor-brukere skal bruke Sensordashboard for å sensurere.');
          return;
        }
        throw new Error(body.message || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setSessions(data.sessions || []);
      setCourses(data.courses || []);
      setSummary(data.summary || null);
    } catch (e: any) {
      setError(e.message || 'Kunne ikke hente oversikt');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  function toggleExpanded(courseId: string) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(courseId)) next.delete(courseId); else next.add(courseId);
      return next;
    });
  }

  const visibleCourses = filter === 'all'
    ? courses
    : courses.filter(c => (c.counts[filter as SessionStatus] || 0) > 0);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-gray-500 py-8 justify-center">
        <Loader2 className="h-5 w-5 animate-spin" />
        <span>Henter oversikt over besvarelser til sensur…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <div className="flex items-start gap-2 text-red-800">
          <AlertCircle className="h-5 w-5 mt-0.5" />
          <div>
            <div className="font-semibold">Kunne ikke laste inn</div>
            <div className="text-sm mt-1">{error}</div>
          </div>
        </div>
        <button onClick={load} className="mt-3 btn btn-secondary btn-sm">
          <RefreshCw className="h-4 w-4 mr-1" /> Prøv igjen
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header + refresh */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Til sensur</h2>
          <p className="text-sm text-gray-600 mt-1">
            Kurs med innleverte besvarelser som venter på sensur. Klikk piltegnet for å se enkeltkandidater,
            eller «Åpne kurs» for å gå til kandidatlista med sensur-filter.
          </p>
        </div>
        <button onClick={load} className="btn btn-secondary btn-sm">
          <RefreshCw className="h-4 w-4 mr-1" /> Oppdater
        </button>
      </div>

      {/* Summary chips (filter) */}
      {summary && summary.total > 0 && (
        <div className="flex flex-wrap gap-2 text-sm">
          <StatusChip
            active={filter === 'all'}
            label={`Alle kurs (${summary.courses_total})`}
            color="bg-gray-100 text-gray-800 border-gray-300"
            onClick={() => setFilter('all')}
          />
          {summary.unassigned > 0 && (
            <StatusChip
              active={filter === 'unassigned'}
              label={`Sensor mangler (${summary.unassigned})`}
              color={STATUS_META.unassigned.color}
              onClick={() => setFilter('unassigned')}
            />
          )}
          {summary.no_progress > 0 && (
            <StatusChip
              active={filter === 'no_progress'}
              label={`Ikke påbegynt (${summary.no_progress})`}
              color={STATUS_META.no_progress.color}
              onClick={() => setFilter('no_progress')}
            />
          )}
          {summary.in_progress > 0 && (
            <StatusChip
              active={filter === 'in_progress'}
              label={`Underveis (${summary.in_progress})`}
              color={STATUS_META.in_progress.color}
              onClick={() => setFilter('in_progress')}
            />
          )}
          {summary.ready_to_finalize > 0 && (
            <StatusChip
              active={filter === 'ready_to_finalize'}
              label={`Klar til finalisering (${summary.ready_to_finalize})`}
              color={STATUS_META.ready_to_finalize.color}
              onClick={() => setFilter('ready_to_finalize')}
            />
          )}
          {summary.no_answers > 0 && (
            <StatusChip
              active={filter === 'no_answers'}
              label={`Ingen svar (${summary.no_answers})`}
              color={STATUS_META.no_answers.color}
              onClick={() => setFilter('no_answers')}
            />
          )}
        </div>
      )}

      {/* Empty state */}
      {courses.length === 0 && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-6 text-center">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
          <div className="font-semibold text-emerald-900">Ingen kurs venter på sensur</div>
          <div className="text-sm text-emerald-800 mt-1">
            Alt er sensurert — eller det finnes ingen innleverte prøver enda.
          </div>
        </div>
      )}

      {/* Course table — én rad per kurs */}
      {visibleCourses.length > 0 && (
        <div className="border border-gray-200 rounded-lg overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 bg-white">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                <th className="px-4 py-3 w-8"></th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Kurs</th>
                <th className="px-4 py-3">Fremdrift</th>
                <th className="px-4 py-3">Sensor</th>
                <th className="px-4 py-3">Eldste innlevering</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-sm">
              {visibleCourses.map(c => {
                const meta = STATUS_META[c.course_status];
                const isExpanded = expanded.has(c.course_instance_id);
                const courseSessions = sessions.filter(s => s.course_instance_id === c.course_instance_id);
                return (
                  <>
                    <tr key={c.course_instance_id} className="hover:bg-gray-50">
                      <td className="px-2 py-3 align-top">
                        <button
                          onClick={() => toggleExpanded(c.course_instance_id)}
                          className="p-1 rounded hover:bg-gray-200 text-gray-500"
                          title={isExpanded ? 'Skjul kandidater' : 'Vis kandidater'}
                          aria-label={isExpanded ? 'Skjul kandidater' : 'Vis kandidater'}
                        >
                          {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${meta.color}`}
                          title={meta.help}
                        >
                          {meta.icon}
                          {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-gray-900">{c.course_instance_name}</div>
                        {c.exam_set_name && (
                          <div className="text-xs text-gray-500 mt-0.5">{c.exam_set_name}</div>
                        )}
                        {c.member_name && (
                          <div className="text-xs text-gray-400 mt-0.5">{c.member_name}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="font-medium text-gray-900">
                          <span className="text-amber-700">{c.awaiting_sensur}</span>
                          <span className="text-gray-400"> av </span>
                          <span>{c.course_total_candidates || c.awaiting_sensur}</span>
                          <span className="text-gray-500 font-normal"> venter sensur</span>
                        </div>
                        {c.awaiting_sensur !== c.course_total_candidates && c.course_total_candidates > 0 && (
                          <div className="text-xs text-gray-500 mt-0.5">
                            {c.course_total_candidates - c.awaiting_sensur} ferdig
                          </div>
                        )}
                        {/* Breakdown-tag per status når det er mix */}
                        {Object.values(c.counts).filter(n => n > 0).length > 1 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {(Object.keys(c.counts) as SessionStatus[])
                              .filter(k => c.counts[k] > 0)
                              .map(k => (
                                <span
                                  key={k}
                                  className={`inline-flex items-center text-[10px] px-1.5 py-0.5 rounded ${STATUS_META[k].color}`}
                                  title={STATUS_META[k].help}
                                >
                                  {STATUS_META[k].label}: {c.counts[k]}
                                </span>
                              ))}
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {c.assigned_sensors.length === 0 ? (
                          <span className="text-red-600 text-xs italic">Ingen tildelt</span>
                        ) : (
                          <ul className="space-y-0.5">
                            {c.assigned_sensors.map(sen => (
                              <li key={sen.id} className="flex items-center gap-1 text-gray-700">
                                <Users className="h-3 w-3 text-gray-400" />
                                <span>{sen.full_name || sen.email}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-gray-700 whitespace-nowrap">
                        {formatDate(c.oldest_submitted_at)}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex flex-col gap-1 items-end">
                          {c.course_status === 'unassigned' && (
                            <Link
                              to={`/course-instances/${c.course_instance_id}?tab=sensors`}
                              className="text-xs font-medium text-red-700 hover:underline flex items-center gap-0.5"
                            >
                              Tildel sensor <ChevronRight className="h-3 w-3" />
                            </Link>
                          )}
                          <Link
                            to={`/course-instances/${c.course_instance_id}?tab=candidates&filter=awaiting_sensur`}
                            className="text-xs font-medium text-kf-primary hover:underline flex items-center gap-0.5"
                          >
                            Åpne kurs <ChevronRight className="h-3 w-3" />
                          </Link>
                        </div>
                      </td>
                    </tr>
                    {/* Ekspandert kandidat-detalj */}
                    {isExpanded && courseSessions.map(s => {
                      const sMeta = STATUS_META[s.status];
                      return (
                        <tr key={s.session_id} className="bg-gray-50 border-t border-gray-100">
                          <td className="px-2 py-2 align-top"></td>
                          <td className="px-4 py-2 align-top">
                            <span
                              className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${sMeta.color}`}
                              title={sMeta.help}
                            >
                              {sMeta.label}
                            </span>
                          </td>
                          <td className="px-4 py-2 align-top text-xs text-gray-600" colSpan={2}>
                            <code className="bg-white px-1.5 py-0.5 rounded border border-gray-200">
                              {s.candidate_code || '—'}
                            </code>
                            <span className="ml-2 text-gray-500">
                              {s.graded_answers}/{s.total_answers} svar sensurert
                            </span>
                          </td>
                          <td className="px-4 py-2 align-top text-xs text-gray-600 whitespace-nowrap">
                            Innlevert {formatDate(s.submitted_at)}
                          </td>
                          <td className="px-4 py-2 align-top">
                            <div className="w-20 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                              <div
                                className={`h-full ${
                                  s.progress_pct === 100
                                    ? 'bg-emerald-500'
                                    : s.progress_pct > 0
                                    ? 'bg-kf-blue-500'
                                    : 'bg-gray-300'
                                }`}
                                style={{ width: `${s.progress_pct}%` }}
                              />
                            </div>
                          </td>
                          <td className="px-4 py-2 align-top"></td>
                        </tr>
                      );
                    })}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Filter viste ingen (men totalt > 0) */}
      {courses.length > 0 && visibleCourses.length === 0 && (
        <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600 flex items-center gap-2">
          <Info className="h-4 w-4" />
          Ingen kurs matcher valgt status. Fjern filteret for å se alle.
        </div>
      )}
    </div>
  );
}

function StatusChip({
  active,
  label,
  color,
  onClick
}: {
  active: boolean;
  label: string;
  color: string;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={`inline-flex items-center px-3 py-1 rounded-full border text-xs font-medium transition ${
        active ? color + ' ring-2 ring-offset-1 ring-kf-primary' : color + ' opacity-70 hover:opacity-100'
      }`}
    >
      {label}
    </button>
  );
}
